import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "trip-info-section" }
const _hoisted_2 = { class: "info-grid" }
const _hoisted_3 = { class: "info-label" }
const _hoisted_4 = { class: "info-value" }
const _hoisted_5 = {
  key: 0,
  class: "status-container"
}
const _hoisted_6 = { class: "status-value" }
const _hoisted_7 = {
  key: 1,
  class: "nav-container"
}
const _hoisted_8 = { class: "nav-value" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  key: 2,
  class: "date-value"
}
const _hoisted_11 = {
  key: 3,
  class: "review-container"
}
const _hoisted_12 = {
  key: 4,
  class: "waybill-container"
}
const _hoisted_13 = ["href"]
const _hoisted_14 = {
  key: 1,
  class: "no-data"
}
const _hoisted_15 = {
  key: 6,
  class: "object-value"
}

import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { formatDateWithTime } from '@/helpers/date'
import { commonCase, normalizeFalsy } from '@/helpers/strings'
import { normalizeUserName } from '@/helpers/user'
import { normalizeEvCarName } from '@/helpers/car-profile'
import { CopyDocument } from '@element-plus/icons-vue'
import { useAuthStore } from '@/store/auth'
import { useEvTripsStore } from '@/store/evTrips'
import { EvTripExtended, EvTripStatus } from '@/models/evTrip'
import { getEvTripWaybill, cancelEvTrip, endEvTrip } from '@/api/ev-trips'


export default /*@__PURE__*/_defineComponent({
  __name: 'DetailsSection',
  props: {
    trip: {}
  },
  setup(__props: any) {

const props = __props
const router = useRouter()
const store = useEvTripsStore()
const authStore = useAuthStore()

const hasAdminPermission = computed(() => {
  const user = authStore.session?.user
  console.log('hasAdminPermission evaluated. user.ev_role:', user?.ev_role)
  if (!user) return false
  return user.ev_role === 'admin' || user.ev_role === 'super_admin'
})

const openWaybillLoading = ref(false)
const showWaybillViewer = ref(false)
const waybillUrl = ref('')

const navKeys: (keyof EvTripExtended)[] = ['renter', 'ev_car_profile']
const dateKeys: (keyof EvTripExtended)[] = [
  'start_date',
  'end_date',
  'reservation_start_time',
  'reservation_end_time',
  'created_at',
  'updated_at',
  'start_timer_free_pause',
  'end_timer_free_pause',
]

const includedKeys: (keyof EvTripExtended)[] = [
  'id',
  'status',
  'start_date',
  'end_date',
  'pricing_type',
  'renter',
  'ev_car_profile',
  // 'energy_level',
  // 'distance_level',
  'start_timer_free_pause',
  'end_timer_free_pause',
  'start_location',
  'end_location',
  'pause_used',
  'total_trip_distance',
  'waybill',
  'review',
  //delete
  // 'created_at',
  // 'updated_at',
  // 'start_timer_pretrip_photo',
  // 'end_timer_pretrip_photo',
  // 'waybill_sent',
]

async function onOpenWaybill() {
  if (!props.trip?.id) return
  openWaybillLoading.value = true
  try {
    const response = await getEvTripWaybill(props.trip.id)
    const url = response?.data?.data?.url ?? ''
    if (!url) throw new Error('Waybill URL missing')

    waybillUrl.value = url
    showWaybillViewer.value = true
  } catch (e) {
    console.error(e)
    ElMessage.error('Waybill not available')
  } finally {
    openWaybillLoading.value = false
  }
}

const tableData = computed(() => {
  const baseData = includedKeys.map(key => ({
    key,
    value: props.trip[key],
  }))

  return baseData
})

const copyToClipboard = (value: string) => {
  navigator.clipboard
    .writeText(value)
    .then(() => {
      ElMessage.success('Trip ID copied to clipboard')
    })
    .catch(() => {
      ElMessage.error('Failed to copy ID')
    })
}

const getReviewScore = () => {
  const rating =
    props.trip.ev_trip_rating?.rating ||
    props.trip.rating ||
    Number(props.trip.review)
  if (rating === null || rating === undefined || isNaN(Number(rating))) return 0
  // Ensure the value is between 0 and 5
  return Math.min(Math.max(Number(rating), 0), 5)
}

const resendWaybill = async () => {
  try {
    await store.resendEvTripWaybill(props.trip.id)
    ElMessage.success('Waybill resent')
  } catch {
    ElMessage.error('Error resending waybill')
  }
}

const cancelTrip = async () => {
  try {
    await cancelEvTrip(props.trip.id)
    ElMessage.success('Trip cancelled by admin')
    store.getEvTripDetails(props.trip.id)
  } catch {
    ElMessage.error('Error cancelling trip')
  }
}

const endTrip = async () => {
  try {
    await endEvTrip(props.trip.id)
    ElMessage.success('Trip marked as finished')
    store.getEvTripDetails(props.trip.id)
  } catch {
    ElMessage.error('Error finishing trip')
  }
}

const goToEntityDetails = (
  key: 'host' | 'renter' | 'ev_car_profile',
  id: string
) => {
  if (key === 'renter' || key === 'host') router.push(`/ev-users/${id}`)
  if (key === 'ev_car_profile') router.push(`/ev-vehicles/${id}`)
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_rate = _resolveComponent("el-rate")!
  const _component_el_checkbox = _resolveComponent("el-checkbox")!
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_image_viewer = _resolveComponent("el-image-viewer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[4] || (_cache[4] = _createElementVNode("h4", { class: "section-title" }, "TRIP INFO", -1)),
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tableData.value, ({ key, value }) => {
          return (_openBlock(), _createElementBlock("div", {
            key: key,
            class: "info-item"
          }, [
            _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(commonCase)(key, true)), 1),
            _createElementVNode("div", _hoisted_4, [
              (key === 'status')
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createElementVNode("span", _hoisted_6, _toDisplayString(value), 1),
                    (
                  ['ongoing', 'pending'].includes(String(value)) &&
                    hasAdminPermission.value
                )
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createVNode(_component_el_button, {
                            type: "danger",
                            size: "medium",
                            onClick: cancelTrip
                          }, {
                            default: _withCtx(() => _cache[1] || (_cache[1] = [
                              _createTextVNode(" Cancelled ")
                            ])),
                            _: 1,
                            __: [1]
                          }),
                          _createVNode(_component_el_button, {
                            type: "success",
                            size: "medium",
                            onClick: endTrip
                          }, {
                            default: _withCtx(() => _cache[2] || (_cache[2] = [
                              _createTextVNode(" Finished ")
                            ])),
                            _: 1,
                            __: [2]
                          })
                        ], 64))
                      : _createCommentVNode("", true)
                  ]))
                : (navKeys.includes(key as any))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("span", _hoisted_8, _toDisplayString(key === 'renter'
                    ? _unref(normalizeUserName)(value as any)
                    : _unref(normalizeEvCarName)(value as any)), 1),
                      _createElementVNode("a", {
                        class: "show-link",
                        onClick: ($event: any) => (goToEntityDetails(key as any, (value as any)?.id))
                      }, " show ", 8, _hoisted_9)
                    ]))
                  : (dateKeys.includes(key as any))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(value ? _unref(formatDateWithTime)(String(value)) : '—'), 1))
                    : (key === 'review')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                          _createVNode(_component_el_rate, {
                            "model-value": getReviewScore(),
                            disabled: "",
                            "show-score": "",
                            "text-color": "#ff9900",
                            colors: ['#ff9900', '#ff9900', '#ff9900'],
                            "void-color": "#c6d1de",
                            max: 5,
                            class: "review-rating"
                          }, null, 8, ["model-value"])
                        ]))
                      : (key === 'waybill')
                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                            _createElementVNode("a", {
                              class: "waybill-link",
                              onClick: onOpenWaybill
                            }, " Open Waybill "),
                            _createVNode(_component_el_checkbox, {
                              "model-value": Boolean(props.trip.waybill_sent),
                              disabled: "",
                              class: "status-checkbox large-checkbox"
                            }, null, 8, ["model-value"]),
                            (!props.trip.waybill_sent)
                              ? (_openBlock(), _createBlock(_component_el_button, {
                                  key: 0,
                                  type: "primary",
                                  size: "small",
                                  onClick: resendWaybill
                                }, {
                                  default: _withCtx(() => _cache[3] || (_cache[3] = [
                                    _createTextVNode(" Resend Waybill ")
                                  ])),
                                  _: 1,
                                  __: [3]
                                }))
                              : _createCommentVNode("", true)
                          ]))
                        : (key === 'start_location' || key === 'end_location')
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                              ((value as any)?.lat && (value as any)?.lng)
                                ? (_openBlock(), _createElementBlock("a", {
                                    key: 0,
                                    href: `https://www.google.com/maps?q=${(value as any).lat},${(value as any).lng}`,
                                    target: "_blank",
                                    rel: "noopener noreferrer",
                                    class: "maps-link"
                                  }, " Google Maps ", 8, _hoisted_13))
                                : (_openBlock(), _createElementBlock("span", _hoisted_14, "—"))
                            ], 64))
                          : (typeof value === 'object' && value !== null)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(JSON.stringify(value)), 1))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 7 }, [
                                _createElementVNode("span", {
                                  class: _normalizeClass(["simple-value", { 'id-value': key === 'id' }])
                                }, _toDisplayString(_unref(normalizeFalsy)(value)), 3),
                                (key === 'id')
                                  ? (_openBlock(), _createBlock(_component_el_icon, {
                                      key: 0,
                                      class: "copy-icon",
                                      onClick: ($event: any) => (copyToClipboard(String(value))),
                                      title: "Copy Trip ID"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_unref(CopyDocument))
                                      ]),
                                      _: 2
                                    }, 1032, ["onClick"]))
                                  : _createCommentVNode("", true)
                              ], 64))
            ])
          ]))
        }), 128))
      ])
    ]),
    (showWaybillViewer.value && !!waybillUrl.value)
      ? (_openBlock(), _createBlock(_component_el_image_viewer, {
          key: 0,
          "url-list": [waybillUrl.value],
          onClose: _cache[0] || (_cache[0] = ($event: any) => (showWaybillViewer.value = false))
        }, null, 8, ["url-list"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})